import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { keepPreviousData } from '@tanstack/react-query'
import {
  createAdminOrganization,
  getAdminOrganization,
  getAdminOrganizations,
  updateAdminOrganization,
  type AdminOrganizationListParams,
  type CreateAdminOrganizationPayload,
  type UpdateAdminOrganizationPayload,
} from '@/services/api'

export const organizationsQueryKey = (params: AdminOrganizationListParams) =>
  ['admin-organizations', params] as const

export function useOrganizations(params: AdminOrganizationListParams) {
  return useQuery({
    queryKey: organizationsQueryKey(params),
    queryFn: () => getAdminOrganizations(params),
    placeholderData: keepPreviousData,
  })
}

export function useOrganizationDetail(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['admin-organization', organizationId],
    queryFn: () => getAdminOrganization(organizationId!),
    enabled: !!organizationId,
  })
}

export function useCreateOrganization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateAdminOrganizationPayload) => createAdminOrganization(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-organizations'] })
    },
  })
}

export function useUpdateOrganization(organizationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateAdminOrganizationPayload) =>
      updateAdminOrganization(organizationId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-organization', organizationId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-organizations'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-workflow-list'] })
    },
  })
}
